// NIGHT-DIRECTSTAFF-2026-10-21 §1/§2: facility leads, and the alert-driven rows in Needs a human.
//
// THE CONTRACT (agents window, NIGHT-AGENTS-2026-10-21 §0):
//   * leads.lead_type = 'facility' once their migration is applied; until then form_type facility_request /
//     facility_staffing_request (and the CRM's older facility test, leadKind) says so.
//   * The facility form's fields arrive in raw_payload: facility_name, contact_name, phone, email, discipline, setting,
//     city_state, target_start_date, notes (the need, in their words), source_page. leads.name/phone/email = the contact.
//   * agent_notifications rows facility_request (urgent) and call_requested (high; a candidate with a phone and NO text
//     consent) have conversation_id null, status 'open' until handled, and a detail that ENDS with lead_id=<uuid>.
//     The SECOND facility alert (4 business hours, no staff action) is ALSO reason facility_request (hub ruling (a), 10-04:
//     one facility code); its detail starts "Second alert:". call_requested rows carry candidate_phone. Older no-consent
//     rows say candidate_no_consent. (Corrected by the agents window 10-21 17:30Z: there is no facility_request_overdue.)
//
// THE PIPELINE (status / owner / notes) is the CRM's own: leads.facility_status, facility_owner, facility_notes,
// facility_status_at (migrations/2026-10-21-leads-facility-pipeline.sql, filed for the hub). Until those exist every
// facility lead reads as "new" and the three are not editable -- never a control whose write cannot land.
import { leadKind } from './leadFilters';
import { signedUpFor } from './signedUp';
import { isTestMarked } from './testMark';
import { isMerged } from './duplicates';

export const FACILITY_FORM_TYPES = ['facility_request', 'facility_staffing_request'];
export const FACILITY_STATUSES = { new: 'New', called: 'Called', quoting: 'Quoting', filled: 'Filled', lost: 'Lost' };
export const FACILITY_COLUMNS = ['facility_status', 'facility_owner', 'facility_notes', 'facility_status_at'];
export const NOTE_MAX = 1000;

const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const raw = (l) => (l && l.raw_payload && typeof l.raw_payload === 'object' ? l.raw_payload : {});

export function isFacilityLead(l) {
  if (!l) return false;
  if (s(l.lead_type).toLowerCase() === 'facility') return true;
  if (FACILITY_FORM_TYPES.includes(s(l.form_type).toLowerCase())) return true;
  return leadKind(l) === 'facility';
}

// The fields shown by name, in this order; everything else the form sent is listed under "Also sent". The contact's
// phone and email are never printed on the card (Call / Email use them; "Show phone" reveals it, as elsewhere).
const KNOWN = ['facility_name', 'facility', 'company_name', 'contact_name', 'discipline', 'setting', 'city_state', 'city', 'state',
  'target_start_date', 'start_date', 'notes', 'message'];
const NOT_SHOWN = ['phone', 'email', 'form_type', 'source_site', 'source_page', 'firstName', 'lastName', 'first_name', 'last_name', 'name'];
const pretty = (k) => k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/** Everything a facility lead says, laid out for the card. */
export function facilityFields(l) {
  const r = raw(l);
  const extras = Object.keys(r).sort().filter((k) => !KNOWN.includes(k) && !NOT_SHOWN.includes(k))
    .map((k) => ({ key: k, label: pretty(k), value: typeof r[k] === 'object' ? JSON.stringify(r[k]) : s(r[k]) }))
    .filter((x) => x.value);
  const status = FACILITY_STATUSES[s(l && l.facility_status)] ? s(l.facility_status) : 'new';
  return {
    facility: s(r.facility_name || r.facility || r.company_name) || 'Facility (no name given)',
    contact: s(r.contact_name || (l && l.name)),
    discipline: s(r.discipline || (l && l.discipline)),
    setting: s(r.setting),
    cityState: s(r.city_state) || [s(r.city), s(r.state || (l && l.state))].filter(Boolean).join(', '),
    start: s(r.target_start_date || r.start_date),
    need: s(r.notes || r.message),
    source: signedUpFor(l).label.replace(/^Site form/, 'Facility form'),
    page: s((l && l.source_page) || r.source_page),
    receivedAt: (l && l.created_at) || null,
    status, statusLabel: FACILITY_STATUSES[status],
    owner: s(l && l.facility_owner),
    notes: s(l && l.facility_notes),
    hasPhone: !!s((l && l.phone) || r.phone),
    hasEmail: !!s((l && l.email) || r.email),
    extras,
  };
}

/** The lead an alert row is about: its detail ends with lead_id=<uuid>. */
export function alertLeadId(n) {
  const m = /lead_id=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\s*$/i.exec(s(n && n.detail));
  return m ? m[1].toLowerCase() : null;
}
/** The detail's words, without the leading reason sentence and the trailing lead_id. */
export function alertWords(n) {
  return s(n && n.detail).replace(/\s*lead_id=[0-9a-f-]{36}\s*$/i, '')
    .replace(/^(Inbound facility staffing request — call back\.|Second alert: facility request not actioned in 4 business hours — call\.|Call — no text consent:)\s*/i, '').trim();
}
export const isOpenAlert = (n) => !!n && s(n.status || 'open') === 'open' && !n.handled_at;

const FACILITY_REASONS = ['facility_request', 'facility_request_overdue'];   // the second is never written; kept harmless
export const isSecondAlert = (n) => /^Second alert:/i.test(s(n && n.detail)) || s(n && n.reason) === 'facility_request_overdue';
const CALL_REASONS = ['call_requested', 'candidate_no_consent'];
const t = (iso) => { const x = new Date(iso).getTime(); return Number.isNaN(x) ? null : x; };
export const hoursSince = (iso, now = Date.now()) => { const x = t(iso); return x === null ? null : Math.max(0, Math.floor((now - x) / 3600e3)); };
export function agoText(h) {
  if (h === null) return '';
  if (h < 1) return 'under an hour ago';
  if (h < 48) return `${h} h since it came in`;
  return `${Math.floor(h / 24)} days since it came in`;
}

/**
 * The rows the agents' alerts put in Needs a human, one per lead, facility requests first (overdue first, then the
 * oldest), then the calls. A lead that is handled (processed), a test, or merged away is not listed; an alert whose lead
 * isn't loaded is still listed (by its short id), never dropped. -> [{ key, kind: 'facility'|'call', leadId, lead, alertIds,
 * overdue, at, hours, words }]
 */
export function alertRows({ notifications = [], leads = [], now = Date.now() }) {
  const byId = new Map(leads.map((l) => [String(l.id).toLowerCase(), l]));
  const groups = new Map();
  for (const n of notifications) {
    if (!isOpenAlert(n)) continue;
    const reason = s(n.reason);
    const kind = FACILITY_REASONS.includes(reason) ? 'facility' : CALL_REASONS.includes(reason) ? 'call' : null;
    if (!kind) continue;
    const leadId = alertLeadId(n);
    const key = leadId || `alert:${n.id}`;
    const lead = leadId ? byId.get(leadId) || null : null;
    if (lead && (lead.processed === true || isTestMarked(lead) || isMerged(lead))) continue;
    const g = groups.get(key) || { key, kind, leadId, lead, alertIds: [], overdue: false, at: null, words: '', phone: '' };
    if (kind === 'facility') g.kind = 'facility';
    g.alertIds.push(n.id);
    if (isSecondAlert(n)) g.overdue = true;
    if (!g.phone && s(n.candidate_phone)) g.phone = s(n.candidate_phone);
    const first = (lead && lead.created_at) || n.created_at;
    if (!g.at || (t(first) !== null && t(first) < t(g.at))) g.at = first;
    if (!g.words && !isSecondAlert(n)) g.words = alertWords(n);
    groups.set(key, g);
  }
  const rows = [...groups.values()].map((g) => ({ ...g, hours: hoursSince(g.at, now) }));
  const rank = (r) => (r.kind === 'facility' ? (r.overdue ? 0 : 1) : 2);
  return rows.sort((a, b) => rank(a) - rank(b) || (t(a.at) ?? 0) - (t(b.at) ?? 0));
}

/** The pipeline write: status / owner / notes on one lead, confirmed (zero rows updated is an error). */
export async function saveFacilityPipeline(client, leadId, patch, now = () => new Date()) {
  const p = {};
  if ('status' in patch) { if (!FACILITY_STATUSES[patch.status]) throw new Error('Unknown status'); p.facility_status = patch.status; p.facility_status_at = now().toISOString(); }
  if ('owner' in patch) p.facility_owner = s(patch.owner).slice(0, 120) || null;
  if ('notes' in patch) p.facility_notes = s(patch.notes).slice(0, NOTE_MAX) || null;
  const { data, error } = await client.from('leads').update(p).eq('id', leadId).select('id');
  if (error) throw new Error(error.message || 'Could not save');
  if (!Array.isArray(data) || data.length !== 1) throw new Error('Not saved: the database did not accept the change (are you signed in?)');
  return p;
}
export async function probeFacilityColumns(client) {
  try { const { error } = await client.from('leads').select(FACILITY_COLUMNS.join(','), { head: true }).limit(0); return !error; } catch { return false; }
}

// NIGHT-DIRECTSTAFF-2026-10-22 §3: the Facilities tab sorts new first -- new (or no status yet), then called, quoting, filled,
// lost; newest first within each.
const STATUS_RANK = { new: 0, called: 1, quoting: 2, filled: 3, lost: 4 };
export function facilityNewFirst(a, b) {
  const ra = STATUS_RANK[String((a && a.facility_status) || 'new')] ?? 0;
  const rb = STATUS_RANK[String((b && b.facility_status) || 'new')] ?? 0;
  return ra - rb || String((b && b.created_at) || '').localeCompare(String((a && a.created_at) || ''));
}
